import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const MenuTypeContext = React.createContext<"dropdown" | "context">("dropdown");

function DropdownMenu(props: DropdownMenuPrimitive.DropdownMenuProps) {
  return (
    <MenuTypeContext value="dropdown">
      <DropdownMenuPrimitive.Root {...props} />
    </MenuTypeContext>
  );
}

function ContextMenu(props: ContextMenuPrimitive.ContextMenuProps) {
  return (
    <MenuTypeContext value="context">
      <ContextMenuPrimitive.Root {...props} />
    </MenuTypeContext>
  );
}

function MenuTrigger(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>,
) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.Trigger
      : ContextMenuPrimitive.Trigger;

  return <Component {...props} />;
}

function MenuGroup(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Group>,
) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.Group
      : ContextMenuPrimitive.Group;

  return <Component {...props} />;
}

function MenuSub(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>,
) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown" ? DropdownMenuPrimitive.Sub : ContextMenuPrimitive.Sub;

  return <Component {...props} />;
}

function MenuRadioGroup(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>,
) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.RadioGroup
      : ContextMenuPrimitive.RadioGroup;

  return <Component {...props} />;
}

function MenuPortal(
  props: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>,
) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.Portal
      : ContextMenuPrimitive.Portal;

  return <Component {...props} />;
}

function MenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.SubTrigger
      : ContextMenuPrimitive.SubTrigger;

  return (
    <Component
      className={cn(
        "flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden focus:bg-slate-4 dark:focus:bg-slatedark-6 data-[state=open]:bg-slate-app [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        inset && "pl-8",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ml-auto" />
    </Component>
  );
}

function MenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.SubContent
      : ContextMenuPrimitive.SubContent;

  return (
    <Component
      className={cn(
        "z-50 min-w-32 overflow-hidden rounded-md border border-slate-dim bg-slate-subtle p-1 text-slate-dim shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  );
}

function MenuContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.Content
      : ContextMenuPrimitive.Content;

  return (
    <MenuPortal>
      <Component
        sideOffset={sideOffset}
        className={cn(
          "z-50 min-w-32 overflow-hidden rounded-md border border-slate-dim bg-slate-subtle p-1 text-slate-dim shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        collisionPadding={8}
        {...props}
      />
    </MenuPortal>
  );
}

function MenuItem({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
}) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.Item
      : ContextMenuPrimitive.Item;

  return (
    <Component
      className={cn(
        "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-slate-4 dark:focus:bg-slatedark-6 focus:text-slate-normal data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        inset && "pl-8",
        className,
      )}
      {...props}
    />
  );
}

function MenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.CheckboxItem
      : ContextMenuPrimitive.CheckboxItem;

  return (
    <Component
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-slate-4 dark:focus:bg-slatedark-6 focus:text-slate-normal data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </Component>
  );
}

function MenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.RadioItem
      : ContextMenuPrimitive.RadioItem;

  return (
    <Component
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors focus:bg-slate-4 dark:focus:bg-slatedark-6 focus:text-slate-normal data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Circle className="h-2 w-2 fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </Component>
  );
}

function MenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.Label
      : ContextMenuPrimitive.Label;

  return (
    <Component
      className={cn(
        "px-2 py-1.5 text-xs font-medium",
        inset && "pl-8",
        className,
      )}
      {...props}
    />
  );
}

function MenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  const type = React.use(MenuTypeContext);
  const Component =
    type === "dropdown"
      ? DropdownMenuPrimitive.Separator
      : ContextMenuPrimitive.Separator;

  return (
    <Component
      className={cn(
        "-mx-1 my-1 h-px bg-slate-6 dark:bg-slatedark-6",
        className,
      )}
      {...props}
    />
  );
}

function MenuShortcut({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  ContextMenu,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuCheckboxItem,
  MenuRadioItem,
  MenuLabel,
  MenuSeparator,
  MenuShortcut,
  MenuGroup,
  MenuSub,
  MenuSubContent,
  MenuSubTrigger,
  MenuRadioGroup,
};
